import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import CitaFormModal from "../components/CitaFormModal";
import { crearCitaVacia, crearFechaHora, crearPayloadCita, obtenerPartesFechaHora } from "../../../dominio/cita";
import { useApi } from "../../../shared/api/api";
import AlertMessage from "../../../shared/components/AlertMessage";
import { useAutoDismissMessage } from "../../../shared/hooks/useAutoDismissMessage";
import { formatCRC, formatDate } from "../../../shared/utils/format";

const monthFormatter = new Intl.DateTimeFormat("es-CR", {
  month: "long",
  year: "numeric"
});

const weekdayFormatter = new Intl.DateTimeFormat("es-CR", {
  weekday: "short"
});

const pad2 = (value) => String(value).padStart(2, "0");

const toDateKey = (date) => (
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`
);

const parseAppointmentDate = (fecha) => {
  if (!fecha) return null;

  const date = new Date(String(fecha).replace(" ", "T"));
  return Number.isNaN(date.getTime()) ? null : date;
};

const getMonthRange = (date) => {
  const start = new Date(date.getFullYear(), date.getMonth(), 1);
  const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);

  return {
    inicio: toDateKey(start),
    fin: toDateKey(end)
  };
};

const addDays = (date, amount) => {
  const next = new Date(date);
  next.setDate(date.getDate() + amount);
  return next;
};

const startOfWeek = (date) => {
  const mondayIndex = (date.getDay() + 6) % 7;
  return addDays(date, -mondayIndex);
};

const getViewRange = (viewMode, date) => {
  if (viewMode === "day") {
    const key = toDateKey(date);
    return { inicio: key, fin: key };
  }

  if (viewMode === "week") {
    const start = startOfWeek(date);
    return {
      inicio: toDateKey(start),
      fin: toDateKey(addDays(start, 6))
    };
  }

  return getMonthRange(date);
};

const getViewTitle = (viewMode, date) => {
  if (viewMode === "day") {
    return formatDate(`${toDateKey(date)}T00:00:00`);
  }

  if (viewMode === "week") {
    const start = startOfWeek(date);
    const end = addDays(start, 6);
    return `${formatDate(`${toDateKey(start)}T00:00:00`)} - ${formatDate(`${toDateKey(end)}T00:00:00`)}`;
  }

  return monthFormatter.format(date);
};

const buildCalendarDays = (visibleMonth) => {
  const year = visibleMonth.getFullYear();
  const month = visibleMonth.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const mondayIndex = (firstDay.getDay() + 6) % 7;
  const calendarStart = new Date(year, month, 1 - mondayIndex);
  const totalCells = Math.ceil((mondayIndex + lastDay.getDate()) / 7) * 7;

  return Array.from({ length: totalCells }, (_, index) => {
    const day = new Date(calendarStart);
    day.setDate(calendarStart.getDate() + index);

    return {
      date: day,
      key: toDateKey(day),
      dayNumber: day.getDate(),
      isCurrentMonth: day.getMonth() === month,
      isToday: toDateKey(day) === toDateKey(new Date())
    };
  });
};

const getAppointmentTime = (fecha) => {
  const date = parseAppointmentDate(fecha);
  if (!date) return "";

  return new Intl.DateTimeFormat("es-CR", {
    hour: "2-digit",
    minute: "2-digit"
  }).format(date);
};

const getAppointmentSlot = (fecha) => {
  const date = parseAppointmentDate(fecha);
  if (!date) return null;

  return (date.getHours() * 60) + (date.getMinutes() >= 30 ? 30 : 0);
};

const formatSlotLabel = (slot) => {
  const hour = Math.floor(slot / 60);
  const minute = slot % 60;
  const hour12 = hour % 12 || 12;
  return `${hour12}:${pad2(minute)} ${hour < 12 ? "a. m." : "p. m."}`;
};

const formatSlotValue = (slot) => {
  const hour = Math.floor(slot / 60);
  const minute = slot % 60;
  return `${pad2(hour)}:${pad2(minute)}`;
};

export default function Agenda() {
  const navigate = useNavigate();
  const [visibleMonth, setVisibleMonth] = useState(() => new Date());
  const [viewMode, setViewMode] = useState("week");
  const [citas, setCitas] = useState([]);
  const [pacientes, setPacientes] = useState([]);
  const [tiposCita, setTiposCita] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDay, setSelectedDay] = useState(() => toDateKey(new Date()));
  const [selectedRange, setSelectedRange] = useState(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [pacienteBusqueda, setPacienteBusqueda] = useState("");
  const [mensaje, setMensaje] = useState(null);
  const [form, setForm] = useState(crearCitaVacia);
  const selectionRangeRef = useRef(null);
  const { apiFetch } = useApi();
  useAutoDismissMessage(mensaje, () => setMensaje(null));

  const selectedDate = useMemo(() => {
    const date = new Date(`${selectedDay}T00:00:00`);
    return Number.isNaN(date.getTime()) ? new Date() : date;
  }, [selectedDay]);

  const visibleDate = viewMode === "month" ? visibleMonth : selectedDate;
  const visibleRange = useMemo(() => getViewRange(viewMode, visibleDate), [viewMode, visibleDate]);

  const cargarCitas = useCallback(async () => {
    setLoading(true);

    try {
      const data = await apiFetch(`/citas.php?action=listByDate&inicio=${visibleRange.inicio} 00:00:00&fin=${visibleRange.fin} 23:59:59`);
      setCitas(data || []);
    } finally {
      setLoading(false);
    }
  }, [apiFetch, visibleRange]);

  useEffect(() => {
    cargarCitas();
  }, [cargarCitas]);

  const cargarPacientes = useCallback(async () => {
    if (pacientes.length > 0) return;

    const data = await apiFetch("/pacientes.php?action=list");
    setPacientes(data || []);
  }, [apiFetch, pacientes.length]);

  const cargarTiposCita = useCallback(async () => {
    const data = await apiFetch("/tiposcita.php?action=list");
    setTiposCita(data || []);
  }, [apiFetch]);

  useEffect(() => {
    cargarTiposCita();
  }, [cargarTiposCita]);

  const diasCalendario = useMemo(() => buildCalendarDays(visibleMonth), [visibleMonth]);

  const diasSemana = useMemo(() => {
    const start = startOfWeek(selectedDate);
    return Array.from({ length: 7 }, (_, index) => {
      const day = addDays(start, index);

      return {
        date: day,
        key: toDateKey(day),
        dayNumber: day.getDate(),
        label: weekdayFormatter.format(day).replace(".", ""),
        isToday: toDateKey(day) === toDateKey(new Date())
      };
    });
  }, [selectedDate]);

  const citasPorDia = useMemo(() => {
    return citas.reduce((groups, cita) => {
      const date = parseAppointmentDate(cita.Fecha);
      if (!date) return groups;

      const key = toDateKey(date);
      if (!groups[key]) groups[key] = [];
      groups[key].push(cita);
      return groups;
    }, {});
  }, [citas]);

  const citasSeleccionadas = useMemo(() => {
    return [...(citasPorDia[selectedDay] || [])].sort((a, b) => {
      const fechaA = parseAppointmentDate(a.Fecha)?.getTime() || 0;
      const fechaB = parseAppointmentDate(b.Fecha)?.getTime() || 0;
      return fechaA - fechaB;
    });
  }, [citasPorDia, selectedDay]);

  const cambiarPeriodo = (amount) => {
    if (viewMode === "day") {
      const next = addDays(selectedDate, amount);
      setSelectedDay(toDateKey(next));
      setVisibleMonth(new Date(next.getFullYear(), next.getMonth(), 1));
      return;
    }

    if (viewMode === "week") {
      const next = addDays(selectedDate, amount * 7);
      setSelectedDay(toDateKey(next));
      setVisibleMonth(new Date(next.getFullYear(), next.getMonth(), 1));
      return;
    }

    setVisibleMonth((current) => {
      const next = new Date(current.getFullYear(), current.getMonth() + amount, 1);
      setSelectedDay(toDateKey(new Date(next.getFullYear(), next.getMonth(), 1)));
      return next;
    });
  };

  const irAHoy = () => {
    const hoy = new Date();
    setVisibleMonth(new Date(hoy.getFullYear(), hoy.getMonth(), 1));
    setSelectedDay(toDateKey(hoy));
  };

  const cambiarVista = (mode) => {
    setViewMode(mode);
    if (mode === "month") {
      setVisibleMonth(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1));
    }
  };

  const getEtiquetaPaciente = (paciente) => {
    const nombre = paciente?.Nombre || "";
    const telefono = paciente?.Telefono ? ` - ${paciente.Telefono}` : "";
    return `${nombre}${telefono}`;
  };

  const abrirNuevaCita = async () => {
    await Promise.all([cargarPacientes(), tiposCita.length > 0 ? Promise.resolve() : cargarTiposCita()]);
    const selectedStartSlot = selectedRange?.dayKey === selectedDay
      ? Math.min(selectedRange.startSlot, selectedRange.endSlot)
      : null;
    const fecha = selectedStartSlot !== null
      ? `${selectedDay}T${formatSlotValue(selectedStartSlot)}`
      : crearFechaHora({ ...obtenerPartesFechaHora(""), date: selectedDay });

    setForm(crearCitaVacia({ Fecha: fecha }));
    setPacienteBusqueda("");
    setEditMode(false);
    setShowFormModal(true);
  };

  const abrirNuevaCitaEnHorario = async (dayKey, slot) => {
    await Promise.all([cargarPacientes(), tiposCita.length > 0 ? Promise.resolve() : cargarTiposCita()]);

    const nextDate = new Date(`${dayKey}T00:00:00`);
    setSelectedDay(dayKey);
    setSelectedRange({ dayKey, startSlot: slot, endSlot: slot });
    if (!Number.isNaN(nextDate.getTime())) {
      setVisibleMonth(new Date(nextDate.getFullYear(), nextDate.getMonth(), 1));
    }

    setForm(crearCitaVacia({ Fecha: `${dayKey}T${formatSlotValue(slot)}` }));
    setPacienteBusqueda("");
    setEditMode(false);
    setShowFormModal(true);
  };

  const iniciarSeleccionHorario = (dayKey, slot) => {
    const range = { dayKey, startSlot: slot, endSlot: slot };
    setSelectedDay(dayKey);
    setSelectedRange(range);
    selectionRangeRef.current = range;
  };

  const extenderSeleccionHorario = (dayKey, slot) => {
    if (!selectionRangeRef.current) return;

    setSelectedRange((current) => {
      if (!current || current.dayKey !== dayKey) return current;
      const next = { ...current, endSlot: slot };
      selectionRangeRef.current = next;
      return next;
    });
  };

  const finalizarSeleccionHorario = async (dayKey, slot) => {
    if (!selectionRangeRef.current) {
      await abrirNuevaCitaEnHorario(dayKey, slot);
      return;
    }

    const range = selectionRangeRef.current.dayKey === dayKey
      ? { ...selectionRangeRef.current, endSlot: slot }
      : { dayKey, startSlot: slot, endSlot: slot };
    const startSlot = Math.min(range.startSlot, range.endSlot);
    const endSlot = Math.max(range.startSlot, range.endSlot);

    setSelectedRange({ dayKey, startSlot, endSlot });
    selectionRangeRef.current = null;
    setSelectedRange({ dayKey, startSlot, endSlot });
  };

  const isHorarioMarcado = (dayKey, slot) => {
    if (!selectedRange || selectedRange.dayKey !== dayKey) return false;

    const startSlot = Math.min(selectedRange.startSlot, selectedRange.endSlot);
    const endSlot = Math.max(selectedRange.startSlot, selectedRange.endSlot);
    return slot >= startSlot && slot <= endSlot;
  };

  const abrirEditarCita = async (cita) => {
    await Promise.all([cargarPacientes(), tiposCita.length > 0 ? Promise.resolve() : cargarTiposCita()]);

    setForm({
      ...cita,
      IVA: Number(cita.IVA) || 0
    });
    setPacienteBusqueda(cita.PacienteNombre || "");
    setSelectedRange(null);
    setEditMode(true);
    setShowFormModal(true);
  };

  const abrirPerfilPaciente = (cita, event) => {
    event.stopPropagation();

    if (!cita?.IdPaciente) return;

    navigate(`/pacientes/${cita.IdPaciente}/perfil`, {
      state: { from: "/agenda", label: "agenda" }
    });
  };

  const manejarTeclaPerfilPaciente = (cita, event) => {
    if (event.key !== "Enter" && event.key !== " ") return;

    event.preventDefault();
    abrirPerfilPaciente(cita, event);
  };

  const cerrarFormModal = () => {
    setShowFormModal(false);
    setEditMode(false);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "IdTiposCita") {
      const tipo = tiposCita.find((t) => t.IdTiposCita.toString() === value);
      if (tipo) {
        setForm((current) => ({
          ...current,
          IdTiposCita: value,
          Descuento: tipo.Descuento
        }));
        return;
      }
    }

    if (name === "IVA" && type === "checkbox") {
      setForm((current) => ({ ...current, IVA: checked ? 4 : 0 }));
      return;
    }

    setForm((current) => ({ ...current, [name]: value }));
  };

  const handlePacienteChange = (e) => {
    const value = e.target.value;
    const paciente = pacientes.find((p) => {
      const nombre = String(p.Nombre || "").toLowerCase();
      const etiqueta = getEtiquetaPaciente(p).toLowerCase();
      const busqueda = value.toLowerCase();
      return etiqueta === busqueda || nombre === busqueda;
    });

    setPacienteBusqueda(value);
    setForm((current) => ({ ...current, IdPaciente: paciente ? paciente.IdPaciente : "" }));
  };

  const handleFechaHoraChange = (name, value) => {
    const partesActuales = obtenerPartesFechaHora(form.Fecha);
    const nuevasPartes = { ...partesActuales, [name]: value };

    setForm((current) => ({
      ...current,
      Fecha: crearFechaHora(nuevasPartes)
    }));
  };

  const handleCostoChange = (e) => {
    const limpio = e.target.value.replace(/\D/g, "");
    setForm((current) => ({ ...current, Costo: limpio }));
  };

  const guardarCita = async (e) => {
    e.preventDefault();

    if (!form.IdPaciente) {
      setMensaje({ tipo: "danger", texto: "Seleccione un paciente valido de la lista" });
      return;
    }

    const resp = await apiFetch(
      editMode ? "/citas.php?action=update" : "/citas.php?action=add",
      "POST",
      crearPayloadCita(form)
    );

    if (resp?.ok) {
      setMensaje({ tipo: "success", texto: editMode ? "Cita actualizada" : "Cita agregada" });
      setShowFormModal(false);
      setEditMode(false);
      setPacienteBusqueda("");
      await cargarCitas();
    } else {
      setMensaje({ tipo: "danger", texto: resp?.error || "Ocurrio un error" });
    }
  };

  const weekDays = useMemo(() => {
    const monday = new Date(2026, 0, 5);
    return Array.from({ length: 7 }, (_, index) => {
      const date = new Date(monday);
      date.setDate(monday.getDate() + index);
      return weekdayFormatter.format(date).replace(".", "");
    });
  }, []);

  const timeSlots = useMemo(() => Array.from({ length: 33 }, (_, index) => 360 + (index * 30)), []);

  const getCitasPorSlot = (dayKey, slot) => {
    return [...(citasPorDia[dayKey] || [])]
      .filter((cita) => getAppointmentSlot(cita.Fecha) === slot)
      .sort((a, b) => {
        const fechaA = parseAppointmentDate(a.Fecha)?.getTime() || 0;
        const fechaB = parseAppointmentDate(b.Fecha)?.getTime() || 0;
        return fechaA - fechaB;
      });
  };

  return (
    <div className="container-fluid mt-3 px-0 px-md-4 agenda-page">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3 gap-2 px-3 px-md-0">
        <div>
          <h2>Agenda</h2> 
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <div className="agenda-view-switch" role="group" aria-label="Tipo de vista del calendario">
            <button type="button" className={viewMode === "day" ? "active" : ""} onClick={() => cambiarVista("day")}>
              Dia
            </button>
            <button type="button" className={viewMode === "week" ? "active" : ""} onClick={() => cambiarVista("week")}>
              Semana
            </button>
            <button type="button" className={viewMode === "month" ? "active" : ""} onClick={() => cambiarVista("month")}>
              Mes
            </button>
          </div>
          <input
            type="date"
            className="form-control form-control-sm agenda-date-jump"
            value={selectedDay}
            onChange={(e) => {
              const next = new Date(`${e.target.value}T00:00:00`);
              setSelectedDay(e.target.value);
              if (!Number.isNaN(next.getTime())) {
                setVisibleMonth(new Date(next.getFullYear(), next.getMonth(), 1));
              }
            }}
            aria-label="Seleccionar fecha"
          />
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => cambiarPeriodo(-1)}>
            Anterior
          </button>
          <button type="button" className="btn btn-success btn-sm" onClick={irAHoy}>
            Hoy
          </button>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => cambiarPeriodo(1)}>
            Siguiente
          </button>
          <button type="button" className="btn btn-primary btn-sm" onClick={abrirNuevaCita}>
            Agregar cita
          </button>
        </div>
      </div>

      <AlertMessage message={mensaje} onClose={() => setMensaje(null)} />

      <CitaFormModal
        show={showFormModal}
        editMode={editMode}
        form={form}
        pacientes={pacientes}
        tiposCita={tiposCita}
        pacienteBusqueda={pacienteBusqueda}
        onPacienteChange={handlePacienteChange}
        onChange={handleChange}
        onFechaHoraChange={handleFechaHoraChange}
        onCostoChange={handleCostoChange}
        onSubmit={guardarCita}
        onClose={cerrarFormModal}
        getEtiquetaPaciente={getEtiquetaPaciente}
      />

      <div className={`agenda-layout ${viewMode === "day" ? "agenda-layout-day" : ""}`}>
        <section className="card agenda-calendar-card">
          <div className="card-body">
            <div className="agenda-calendar-header">
              <h5 className="mb-0 text-capitalize">{getViewTitle(viewMode, visibleDate)}</h5>
              {loading && (
                <span className="agenda-loading">
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  Cargando
                </span>
              )}
            </div>

            {viewMode === "month" && (
              <>
                <div className="agenda-weekdays" aria-hidden="true">
                  {weekDays.map((day) => (
                    <span key={day} className="text-capitalize">{day}</span>
                  ))}
                </div>

                <div className="agenda-calendar-grid">
                  {diasCalendario.map((day) => {
                    const citasDelDia = citasPorDia[day.key] || [];
                    const visibleAppointments = citasDelDia.slice(0, 3);
                    const remainingCount = citasDelDia.length - visibleAppointments.length;

                    return (
                      <button
                        key={day.key}
                        type="button"
                        className={[
                          "agenda-day",
                          day.isCurrentMonth ? "" : "agenda-day-muted",
                          day.isToday ? "agenda-day-today" : "",
                          selectedDay === day.key ? "agenda-day-selected" : ""
                        ].filter(Boolean).join(" ")}
                        onClick={() => setSelectedDay(day.key)}
                      >
                        <span className="agenda-day-number">{day.dayNumber}</span>
                        <span className="agenda-day-appointments">
                          {visibleAppointments.map((cita) => (
                            <span
                              key={cita.IdCita}
                              role="button"
                              tabIndex={0}
                              className="agenda-appointment-pill"
                              title={`${getAppointmentTime(cita.Fecha)} - ${cita.PacienteNombre || "Paciente"} - ${cita.TipoCitaNombre || "Tipo de cita"}`}
                              onClick={(event) => {
                                event.stopPropagation();
                                abrirEditarCita(cita);
                              }}
                              onKeyDown={(event) => {
                                if (event.key === "Enter" || event.key === " ") {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  abrirEditarCita(cita);
                                }
                              }}
                            >
                              <span
                                className="agenda-appointment-patient"
                                role="link"
                                tabIndex={0}
                                onMouseDown={(event) => event.stopPropagation()}
                                onClick={(event) => abrirPerfilPaciente(cita, event)}
                                onKeyDown={(event) => manejarTeclaPerfilPaciente(cita, event)}
                              >
                                {cita.PacienteNombre || "Paciente"}
                              </span>
                              <small className="agenda-appointment-type">{cita.TipoCitaNombre || "Tipo de cita"}</small>
                            </span>
                          ))}
                          {remainingCount > 0 && (
                            <span className="agenda-more">+{remainingCount} mas</span>
                          )}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            {viewMode === "week" && (
              <div className="agenda-time-grid agenda-time-grid-week">
                <div className="agenda-time-corner"></div>
                {diasSemana.map((day) => (
                  <button
                    key={day.key}
                    type="button"
                    className={[
                      "agenda-time-day-heading",
                      day.isToday ? "agenda-time-day-today" : "",
                      selectedDay === day.key ? "agenda-time-day-selected" : ""
                    ].filter(Boolean).join(" ")}
                    onClick={() => setSelectedDay(day.key)}
                  >
                    <strong>{day.dayNumber}</strong>
                    <span className="text-capitalize">{day.label}</span>
                  </button>
                ))}

                {timeSlots.map((slot) => (
                  <React.Fragment key={slot}>
                    <div className={`agenda-time-label ${slot % 60 === 30 ? "agenda-time-label-half" : ""}`}>
                      {slot % 60 === 0 ? formatSlotLabel(slot) : ""}
                    </div>
                    {diasSemana.map((day) => {
                      const citasHora = getCitasPorSlot(day.key, slot);

                      return (
                        <div
                          key={`${day.key}-${slot}`}
                          className={[
                            "agenda-time-slot",
                            slot % 60 === 30 ? "agenda-time-slot-half" : "",
                            selectedDay === day.key ? "agenda-time-slot-selected" : "",
                            isHorarioMarcado(day.key, slot) ? "agenda-time-slot-marked" : ""
                          ].filter(Boolean).join(" ")}
                          onMouseDown={() => iniciarSeleccionHorario(day.key, slot)}
                          onMouseEnter={() => extenderSeleccionHorario(day.key, slot)}
                          onMouseUp={() => finalizarSeleccionHorario(day.key, slot)}
                        >
                          {citasHora.map((cita) => (
                            <button
                              key={cita.IdCita}
                              type="button"
                              className="agenda-time-appointment"
                              title={`${getAppointmentTime(cita.Fecha)} - ${cita.PacienteNombre || "Paciente"} - ${cita.TipoCitaNombre || "Tipo de cita"}`}
                              onMouseDown={(event) => event.stopPropagation()}
                              onClick={(event) => {
                                event.stopPropagation();
                                abrirEditarCita(cita);
                              }}
                            >
                              <span
                                className="agenda-appointment-patient"
                                role="link"
                                tabIndex={0}
                                onMouseDown={(event) => event.stopPropagation()}
                                onClick={(event) => abrirPerfilPaciente(cita, event)}
                                onKeyDown={(event) => manejarTeclaPerfilPaciente(cita, event)}
                              >
                                {cita.PacienteNombre || "Paciente"}
                              </span>
                              <small className="agenda-appointment-type">{cita.TipoCitaNombre || "Tipo de cita"}</small>
                            </button>
                          ))}
                        </div>
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>
            )}

            {viewMode === "day" && (
              <div className="agenda-time-grid agenda-time-grid-day">
                <div className="agenda-time-corner"></div>
                <div className="agenda-time-day-heading agenda-time-day-selected">
                  <strong>{selectedDate.getDate()}</strong>
                  <span className="text-capitalize">{weekdayFormatter.format(selectedDate).replace(".", "")}</span>
                </div>

                {timeSlots.map((slot) => {
                  const citasHora = getCitasPorSlot(selectedDay, slot);

                  return (
                    <React.Fragment key={slot}>
                      <div className={`agenda-time-label ${slot % 60 === 30 ? "agenda-time-label-half" : ""}`}>
                        {slot % 60 === 0 ? formatSlotLabel(slot) : ""}
                      </div>
                      <div
                        className={[
                          "agenda-time-slot",
                          "agenda-time-slot-day",
                          slot % 60 === 30 ? "agenda-time-slot-half" : "",
                          isHorarioMarcado(selectedDay, slot) ? "agenda-time-slot-marked" : ""
                        ].filter(Boolean).join(" ")}
                        onMouseDown={() => iniciarSeleccionHorario(selectedDay, slot)}
                        onMouseEnter={() => extenderSeleccionHorario(selectedDay, slot)}
                        onMouseUp={() => finalizarSeleccionHorario(selectedDay, slot)}
                      >
                        {citasHora.map((cita) => (
                          <button
                            key={cita.IdCita}
                            type="button"
                            className="agenda-day-time-appointment"
                            onMouseDown={(event) => event.stopPropagation()}
                            onClick={(event) => {
                              event.stopPropagation();
                              abrirEditarCita(cita);
                            }}
                          >
                            <div className="agenda-day-view-time">{getAppointmentTime(cita.Fecha)}</div>
                            <div>
                              <span
                                className="patient-profile-link agenda-appointment-patient"
                                role="link"
                                tabIndex={0}
                                onMouseDown={(event) => event.stopPropagation()}
                                onClick={(event) => abrirPerfilPaciente(cita, event)}
                                onKeyDown={(event) => manejarTeclaPerfilPaciente(cita, event)}
                              >
                                {cita.PacienteNombre || `Paciente ${cita.IdPaciente}`}
                              </span>
                              <div className="agenda-detail-meta">{cita.TipoCitaNombre || "Tipo de cita"}</div>
                              <div className="agenda-detail-meta">{formatCRC(cita.Costo)}</div>
                            </div>
                          </button>
                        ))}
                      </div>
                    </React.Fragment>
                  );
                })}

                {citasSeleccionadas.length === 0 && (
                  <div className="agenda-day-empty-overlay">
                    No hay citas para este dia.
                  </div>
                )}
              </div>
            )}
          </div>
        </section>

        {viewMode !== "day" && (
          <aside className="card agenda-detail-card">
            <div className="card-body">
              <h5 className="card-title mb-3">{formatDate(`${selectedDay}T00:00:00`)}</h5>
              {citasSeleccionadas.length > 0 ? (
                <div className="agenda-detail-list">
                  {citasSeleccionadas.map((cita) => (
                    <article key={cita.IdCita} className="agenda-detail-item">
                      <div className="agenda-detail-time">{getAppointmentTime(cita.Fecha)}</div>
                      <div>
                        <Link
                          className="patient-profile-link"
                          to={`/pacientes/${cita.IdPaciente}/perfil`}
                          state={{ from: "/agenda", label: "agenda" }}
                        >
                          {cita.PacienteNombre || `Paciente ${cita.IdPaciente}`}
                        </Link>
                        <div className="agenda-detail-meta">{cita.TipoCitaNombre || "Tipo de cita"}</div>
                        <div className="agenda-detail-meta">{formatCRC(cita.Costo)}</div>
                      </div>
                    </article>
                  ))}
                </div>
              ) : (
                <div className="agenda-empty">
                  No hay citas para este dia.
                </div>
              )}
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}
